import { DeleteOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { Button, Divider, Table, Typography } from 'antd';
import { type FormItemProps } from 'antd/es/form';
import { FieldArray, useField } from 'formik';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import FormItem from '../../../../../../components/fields/FormItem';
import InputNumberField from '../../../../../../components/fields/InputNumberField';
import TimePickerField from '../../../../../../components/fields/TimePickerField';
import { getOffset } from '../../../../../../utilities/date';
import { StyledExtraConfigurationTableHeader } from '../../../../../shared/AdminSharedUI';

const { Title } = Typography;

export type AvailableTimeSlotFieldArrayProps = {
    name: string;
    label?: string;
    labelForAdd?: string;
    itemProps?: Omit<FormItemProps, 'label' | 'meta' | 'required' | 'children'>;
    limit?: number;
    timeZone?: string;
};

const emptyTimeSlot = {
    slot: null,
    bookingLimit: 0,
};

const AvailableTimeSlotFieldArray = ({
    name,
    label,
    labelForAdd,
    itemProps,
    limit,
    timeZone,
    ...props
}: AvailableTimeSlotFieldArrayProps) => {
    const { t } = useTranslation(['common', 'appointmentModuleDetails']);

    const [field, , { setValue }] = useField({ name });
    const { value } = field;

    const addTimeRange = useCallback(() => {
        setValue([...value, emptyTimeSlot]);
    }, [setValue, value]);

    return (
        <>
            <Divider />
            <StyledExtraConfigurationTableHeader>
                <Title level={5} style={{ fontSize: '14px' }}>
                    {t('appointmentModuleDetails:sections.bookingTimeSlot')}
                </Title>
                <Button icon={<PlusCircleOutlined />} onClick={addTimeRange} type="link">
                    {t('appointmentModuleDetails:actions.addTimeSlot')}
                </Button>
            </StyledExtraConfigurationTableHeader>
            <FormItem {...itemProps} label={label} name={name}>
                <FieldArray
                    {...field}
                    name={name}
                    render={arrayHelpers => (
                        <Table
                            dataSource={value}
                            pagination={false}
                            rowKey={(row, index: number) => `${name}_${index.toString()}`}
                        >
                            <Table.Column
                                dataIndex="start"
                                render={(value, record, index) => (
                                    <TimePickerField format="h:mm a" minuteStep={30} name={`${name}.[${index}].slot`} />
                                )}
                                title={t('appointmentModuleDetails:fields.bookingTimeSlot.slot.label', {
                                    offset: getOffset(t, timeZone),
                                })}
                            />
                            <Table.Column
                                dataIndex="end"
                                render={(value, record, index) => (
                                    <InputNumberField name={`${name}.[${index}].bookingLimit`} />
                                )}
                                title={t('appointmentModuleDetails:fields.bookingTimeSlot.bookingLimit.label')}
                            />
                            <Table.Column
                                key="actions"
                                align="center"
                                render={(_, __, index) => (
                                    <Button
                                        icon={<DeleteOutlined />}
                                        onClick={() => {
                                            arrayHelpers.remove(index);
                                        }}
                                        size="middle"
                                        type="link"
                                    />
                                )}
                                title={t('common:actions.action')}
                                width={100}
                            />
                        </Table>
                    )}
                />
            </FormItem>
        </>
    );
};

export default AvailableTimeSlotFieldArray;
